'use client';

import { Tournament } from '@/lib/Tournaments/types';
import React, { useCallback, useMemo } from 'react';
import Oink from '@/common/Oink';
import Text from '@/components/Text';
import Button from '@/components/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import TournamentsUtils from '@/utils/TournamentsUtils';
import TournamentStatusBadge from '../TournamentStatusBadge';
import TournamentsTimeBadge from '../TournamentsTimeBadge';
import { twMerge } from 'tailwind-merge';
import MemojiList from '@/common/MemojiList';
import Link from 'next/link';
import InstitutionLogo from '@/common/InstitutionLogo';
import StringUtils from '@/utils/StringUtils';
import useInstitution from '@/hooks/useInstitution';
import useTournamentLeaderboard from '@/hooks/useTournamentLeaderboard';
import Loader from '@/components/Loader';
import Image from 'next/image';

interface ITournamentCardProps {
  tournament: Tournament;
  isCurrent?: boolean;
  className?: string;
  onPress?: (id: string) => void;
}

const bgImage = {
  daily: 'bg-gradient-blue',
  weekly: 'bg-gradient-blue-purple',
  monthly: 'bg-gradient-blue-pink',
  get(type: string) {
    return this[type || 'daily'];
  },
};

const bgEffect = {
  daily: 'bg-gradient-green opacity-20',
  weekly: 'bg-gradient-yellow opacity-20',
  monthly: 'bg-gradient-pink opacity-20',
  get(type: string) {
    return this[type || 'daily'];
  },
};

function tournamentNameParts(name: string) {
  const [type, amount, _, title] = name.split(' ');
  return (
    <div className="flex flex-col">
      <div className="font-[200] text-xs text-overlay">
        {type} {title}
      </div>
      <div className="flex gap-2 items-center">
        <Oink />
        <Text size="lg" className="leading-[none] pt-0.5">
          {amount}
        </Text>
      </div>
    </div>
  );
}

const RootCard = React.memo(
  ({ tournament, isCurrent = false, onPress, className }: ITournamentCardProps) => {
    const tournamentType = TournamentsUtils.getTournamentType(tournament.name);
    const { data: institution } = useInstitution(tournament.collegeName as string);
    const { currentPosition, isLoading: isLeaderboardLoading } = useTournamentLeaderboard(
      tournament.id
    );
    const isHighSchool = tournament?.audience === 'HighSchool';
    const isUniversity = tournament?.audience === 'University';

    const totalAllocated =
      tournament.isParticipating &&
      tournament?.portfolio?.reduce((acc, stock) => acc + stock.allocatedNotional, 0);

    const handlePress = () => {
      document.body.scrollTo({ top: 0, behavior: 'smooth' });
      //onPress?.(tournament.id);
    };

    const RenderMyPosition = () => {
      const myPosition = useMemo(() => {
        return {
          current: currentPosition,
          isInTop: Number(currentPosition) <= tournament.prizes.length,
          prize: tournament.prizes.find(
            (pz) =>
              pz.startPlace <= Number(currentPosition) && pz.endPlace >= Number(currentPosition)
          ),
        };
      }, [currentPosition, tournament.prizes.length]);

      if (isLeaderboardLoading) return <Loader className="size-4" />;
      if (!myPosition.current) return null;

      return (
        <div className="flex gap-4">
          <div className="flex flex-col gap-0 leading-tight">
            <Text size="md">{myPosition.current ? `# ${myPosition.current}` : '--'}</Text>
            <Text size="xs" className="text-secondary">
              Your position
            </Text>
          </div>
          <div className="flex flex-col gap-0 leading-tight">
            <Text size="md">
              {!!myPosition.prize?.amount && tournament.status === 'Finished'
                ? StringUtils.USD(myPosition.prize?.amount)
                : '--'}
            </Text>
            <Text size="xs" className="text-secondary">
              Your prize
            </Text>
          </div>
        </div>
      );
    };

    return (
      <Link
        className={twMerge(
          'group relative w-full p-4 rounded-xl overflow-hidden bg-center hover:bg-bottom-left flex flex-col gap-y-4 transition-all duration-300 hover:ring-white/50 hover:opacity-100',
          `hover:${bgImage.get(tournamentType)}`,
          onPress && 'cursor-pointer',
          className,
          isCurrent
            ? `ring-inset ring-2 ring-white/50 opacity-100 ${bgImage.get(tournamentType)}`
            : 'bg-darkglass'
        )}
        href={`/tournaments/${tournament.id}`}
        onClick={handlePress}
      >
        <div className={twMerge('absolute w-full aspect-square bottom-0 -right-1/2', bgEffect.get(tournamentType))} />
        <div className="flex-auto flex justify-between items-center z-10">
          <div title={tournament.status}>{tournamentNameParts(tournament.name)}</div>

          {!!tournament?.numberOfParticipants && (
            <MemojiList
              numMemojis={tournament?.numberOfParticipants}
              maxMemojis={4}
              themeClassName={`bg-secondary`}
            />
          )}
        </div>

        <div className="group flex gap-2 justify-between items-center -mt-2 z-10">
          <div className="flex-none">
            {currentPosition ? (
              <RenderMyPosition />
            ) : (
              <>
                {tournament?.collegeName && institution ? (
                  <Text size="sm">{StringUtils.capitalizePhrase(institution.name)}</Text>
                ) : (
                  <>
                    {isHighSchool ||
                      (isUniversity && (
                        <Text size="sm">
                          {isHighSchool ? 'High School' : 'University'} Tournament
                        </Text>
                      ))}
                  </>
                )}
              </>
            )}
          </div>
          {tournament?.collegeName && institution ? (
            <div className="mix-blend-multiply">
              <InstitutionLogo id={institution.id} width={40} height={48} showName={false} />
            </div>
          ) : (
            <>
              {isHighSchool ||
                (isUniversity && (
                  <Image
                    src="https://cdn.bloomapp.com/tournament/tournaments-default-school.png"
                    alt="Tournament"
                    width={40}
                    height={40}
                  />
                ))}
            </>
          )}
        </div>

        <div className="flex-none flex justify-between items-center z-10">
          {tournament.status == 'Finished' && <TournamentStatusBadge status={tournament.status} />}
          {tournament.status == 'Cancelled' && <TournamentStatusBadge status={tournament.status} />}

          {tournament.status == 'Open' && <TournamentsTimeBadge tournament={tournament} />}
          {tournament.status == 'InProgress' && (
            <TournamentsTimeBadge
              icon={
                <FontAwesomeIcon
                  icon={faCircle}
                  className={twMerge(
                    'size-3',
                    TournamentsUtils.TOURNAMENT_STATUS_ICON_COLOR[tournament.status]
                  )}
                />
              }
              tournament={tournament}
            />
          )}

          {tournament.status === 'Cancelled' && (
            <Button
              type="Secondary"
              size="xs"
              className={`px-4 rounded-full`}
            >
              View tournament
            </Button>
          )}

          {tournament.isParticipating &&
          tournament.status !== 'Finished' &&
          tournament.status !== 'Cancelled' ? (
            <Button
              type="Secondary"
              size="xs"
              className={`px-4 rounded-full`}
            >
              {tournament.status === 'InProgress'
                ? 'Your results'
                : tournament.status === 'Open'
                ? totalAllocated < tournament.budget
                  ? 'Edit stocks'
                  : 'View stocks'
                : 'View portfolio'}
            </Button>
          ) : (
            tournament.status !== 'Finished' &&
            tournament.status !== 'Cancelled' && (
              <Button
                type={tournament.status === 'InProgress' ? 'Secondary' : 'Primary'}
                size="xs"
                className={`px-4 rounded-full`}
              >
                {tournament.status === 'InProgress' ? 'Spectate' : 'Join tournament'}
              </Button>
            )
          )}

          {tournament.status === 'Finished' && <TournamentsTimeBadge tournament={tournament} />}
        </div>
      </Link>
    );
  }
);

RootCard.displayName = 'RootCard';

function CardSkeleton() {
  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col gap-2 bg-darkglass p-4 rounded-xl">
        <div className="flex justify-between">
          <div className="flex-1 space-y-2">
            <div className="bg-darkglass w-1/3 h-4 rounded-xl animate-skeleton"></div>
            <div className="bg-darkglass w-2/3 h-10 rounded-xl animate-skeleton"></div>
          </div>
          <div className="flex-1 bg-darkglass rounded-xl animate-skeleton h-10"></div>
        </div>
        <div className="flex justify-between  animate-skeleton">
          <div className="bg-darkglass w-1/6 h-6 rounded-xl"></div>
          <div className="bg-darkglass w-1/6 h-6 rounded-xl"></div>
        </div>
      </div>
    </div>
  );
}

const TournamentCard = Object.assign(RootCard, { Skeleton: CardSkeleton });

export default TournamentCard;
